import {
    unwrapArray,
    setObjectPrototype,
    suffixNumber,
    isPlainObject,
    unwrapElementWithFunc,
    isString,
    type Nullable, isFunction, type Constructor
} from "../base/Utils";
import {CleanupJobs, Dispatchable, OncePerTickRunner, type RemoveHandle} from "../base/Dispatcher";
import {DOMAttributesMap, NodeAttributes} from "./NodeAttributes";
export type {NodeAttributes};
import {Theme, type ThemeProps} from "./style/Theme";
import type {StyleSheet} from "./Style"; // Type-only on purpose: a runtime edge here would close a cycle

export type SVGTagType = keyof SVGElementTagNameMap;
export type HTMLTagType = keyof HTMLElementTagNameMap;
export type UICleanChild = BaseUIElement | string | number;

// So classes can signal that they can be converted to UI elements, or at least string-represented in the UI
export interface UIRenderable {
    toUI(parent?: BaseUIElement): UICleanChild;
}

// A function is called by cleanChildren (unwrapElementWithFunc) and its result used, so lazy children are children
export type UIChild = Iterable<UIChild> | UICleanChild | UIRenderable | (() => UIChild) | null | undefined | false;
// What cleanChildren leaves: it flattens and resolves functions, but a renderable is only converted at redraw
export type UIResolvedChild = UICleanChild | UIRenderable;
// node.style coerces and applyStyleToNode calls a function value, so both spellings are the value
export type StyleValue<T> = T | number | (() => T | number);
export type StyleObject = {[Key in keyof CSSStyleDeclaration]?: StyleValue<CSSStyleDeclaration[Key]>};

// The onX option each addXListener implies, called with the adder's arguments and then the element.
// An adder that declares its callback open - addChangeListener's `Function` - names no option: the option
// would type nothing, and being on every element it would defeat the narrower ones written by hand
export type EventOptions<Element> = {
    [K in Extract<keyof Element, `add${string}Listener`> as
        Element[K] extends (callback: (...args: infer Args) => any, ...rest: any[]) => any
            ? number extends Args["length"]
                ? never
                : (K extends `add${infer Event}Listener` ? `on${Event}` : never)
            : never]?:
                Element[K] extends (callback: (...args: infer Args) => any, ...rest: any[]) => any
                    ? (...args: [...Args, Element]) => any
                    : never;
};

// Called with the event, then the element itself
export type UIEventHandler = (...args: any[]) => unknown;

// The trailing arguments a node listener forwards to addEventListener
export type NodeListenerOptions = [(boolean | AddEventListenerOptions)?];
export type RefLinkOptions = {
    // Only ever assigned into by applyRef, so anything indexable qualifies
    parent: Dispatchable | unknown[] | Record<string, unknown>;
    name?: string | number;
    key?: string;
};


// Type definitions

// What an element holds. createElement collects the children into an array and resolves a string ref, so
// those two are narrower here than in a tag.
// EventOptions gets no TagType: in a parameter position it would break assignability across tags
export interface UIElementOptions<TagType extends string = HTMLTagType> extends EventOptions<UIElement<any, any, any, any>> {
    children?: UIResolvedChild[];
    title?: UIChild;
    ref?: RefLinkOptions;
    key?: string | number;
    active?: boolean; // Tabs or switchers can put this on children
    tabHref?: string; // A tab area reads this off the panel to link its tab
    nodeType?: TagType;
    className?: string;
    style?: string | StyleObject;
    // These all reach the node through setAttribute, on whatever tag the element is
    // domTitle rather than title, since an element's own title is a child of <head>
    domTitle?: string;
    id?: string | number;
    height?: number | string;
    width?: number | string;
    href?: string;
    theme?: Theme;
    // The element that owns this one, which components read to call back into it. Left open because that
    // is the point: each consumer calls its own owner's methods, so a class narrows this in its own options
    parent?: any;
    styleSheet?: StyleSheet | typeof StyleSheet;
    //[key: string]: any;
}

// What an element passes down to its children, merged at each level. Left open: getExtraContext is the
// extension point, and a subclass adds whatever it wants read further down
export interface UIContext {
    theme?: Theme;
    [key: string]: unknown;
}

// The two createElement normalizes
type NormalizedOptions = "children" | "ref";

// The same options as a tag takes them, which is what the ts-plugin declares for each class from these
export interface WrittenUIElementOptions<TagType extends string = HTMLTagType> extends Omit<UIElementOptions<TagType>, NormalizedOptions> {
    children?: UIChild;
    ref?: RefLinkOptions | string;
}

// Held options turned back into written ones, for a class that respells `options`. Mapped, so only for a
// concrete class: a generic tag infers through the intersection below, not through this. A narrowing of the
// children the respelling inherited - to SVG children, say - is kept, so the tag stays within its base's.
export type WrittenOptions<Options> = {
    [Key in keyof Options as Exclude<Key, NormalizedOptions>]: Options[Key];
} & Pick<WrittenUIElementOptions, NormalizedOptions> & (Options extends {children?: infer Held} ? UICleanChild[] extends Held ? unknown : {children?: Held} : unknown);

// The keys of T that aren't readonly
type WritableKeys<T> = {
    [Key in keyof T]-?: (<G>() => G extends {[P in Key]: T[Key]} ? 1 : 2) extends
        (<G>() => G extends {-readonly [P in Key]: T[Key]} ? 1 : 2) ? Key : never;
}[keyof T];

// The DOM members stem's own options replace, since intersecting narrows away the spelling setAttribute takes
export type OverriddenNodeMembers = "children" | "nodeType" | "style" | "title" | "value" | "min" | "max"
    | "step" | "autofocus" | "id" | "height" | "width" | "href" | "rows"
    // A method is never an attribute, and intersecting one leaves a type nothing satisfies
    | "animate";

// Options land on the node as attributes, so a readonly DOM member can never be one
export type NodeOptions<NodeType> = Partial<Omit<Pick<NodeType, WritableKeys<NodeType>>, OverriddenNodeMembers>>;

export type UIOptions<NodeType extends (SVGElement | HTMLElement), ExtraOptions = {}, TagType extends string = HTMLTagType> = NodeOptions<NodeType> & UIElementOptions<TagType> & ExtraOptions;

// The same as a tag takes it. ExtraOptions stays a bare constituent, so a generic element infers it from the tag.
export type WrittenUIOptions<NodeType extends (SVGElement | HTMLElement), ExtraOptions = {}, TagType extends string = HTMLTagType> = NodeOptions<NodeType> & WrittenUIElementOptions<TagType> & ExtraOptions;

// For a class extending a Stem UI class that doesn't pass its options generic along
export type ElementOptions<ExtraOptions = {}, NodeType extends (SVGElement | HTMLElement) = HTMLElement> = UIOptions<NodeType, ExtraOptions>;

// Same, for a base that has options of its own: they're kept and yours are added on top
export type ExtendedOptions<BaseElement extends {options?: any}, ExtraOptions = {}> = NonNullable<BaseElement["options"]> & ExtraOptions;

// Name the class, not `this`: a polymorphic return type rejects the literal getDefaultOptions returns
export type PartialOptions<Element extends {options?: any}> = Partial<NonNullable<Element["options"]>>;

// A container that only ever holds one kind of element, which neither children type can say
export type ElementContainer<Container extends {options?: any}, ChildType> = Container & {
    children: ChildType[];
    options: NonNullable<Container["options"]> & {children?: ChildType[]};
};

// An element whose style another one writes into, which `string | StyleObject` cannot say
export type StyledElement<Element extends {options?: any}> = Element & {
    options: NonNullable<Element["options"]> & {style: StyleObject};
};

// An element that lays itself out from its size: the attribute may be a percentage, the arithmetic may not
export interface NumericSizeOptions {
    width?: number;
    height?: number;
}

export const RenderStack: BaseUIElement[] = []; //keeps track of objects that are redrawing, to know where to assign refs automatically
export const redrawPerTickRunner = new OncePerTickRunner((obj: BaseUIElement, event?: unknown) => obj.node && obj.redraw(event));

// TODO Maybe get rid of the UI namespace
export interface UINamespace {
    TextElement: typeof TextUIElement;
    Element: typeof UIElement;
    SVGElement: typeof UIElement<any, any, any>;
    createElement<K extends HTMLTagType>(tag: K, options?: WrittenUIElementOptions | null, ...children: UIChild[]): UIElement<{}, HTMLElementTagNameMap[K]>;
    createElement<K extends SVGTagType>(tag: K, options?: WrittenUIElementOptions | null, ...children: UIChild[]): UIElement<{}, SVGElementTagNameMap[K]>;
    createElement<UIClass extends BaseUIElement<any>>(tag: new (options: any) => UIClass, options?: any, ...children: UIChild[]): UIClass;
    createElement(tag: any, options?: WrittenUIElementOptions | null, ...children: UIChild[]): BaseUIElement | null;
    T: (value: string) => BaseUIElement; // Assigned by Translation.ts
    // The return carries whatever was passed, so extra options go on the result, not here
    Primitive: <T extends string = keyof HTMLElementTagNameMap, BaseClassType extends Constructor<UIElement<any, any, any>> = typeof UIElement>(nodeType: T, BaseClass?: BaseClassType) => BaseClassType;
}

export const UI: UINamespace = {} as UINamespace;

export function cleanChildren(children: UIChild): UIResolvedChild[] {
    return unwrapArray(children, unwrapElementWithFunc);
}

// Both probe the way the code they replaced did, optional because a toUI() may answer with nothing
function isElement(child: UIResolvedChild): child is BaseUIElement {
    return Boolean((child as BaseUIElement)?.getNodeType);
}

function isRenderable(child: UIResolvedChild): child is UIRenderable {
    return Boolean((child as UIRenderable)?.toUI);
}

// A child that is not an element becomes one: its own toUI, or its text
function resolveChildElement(child: UIResolvedChild, parent: BaseUIElement): BaseUIElement {
    let element = child;
    if (!isElement(element)) {
        if (isRenderable(element)) {
            element = element.toUI(parent);
        }
        // No toUI(), or toUI() gave back a plain value
        if (!isElement(element)) {
            element = new UI.TextElement(String(element));
        }
    }
    return element;
}

export abstract class BaseUIElement<NodeType extends ChildNode = SVGElement | HTMLElement | Text> extends Dispatchable {
    declare node?: NodeType;
    declare parent?: UIElement;
    declare options?: UIElementOptions<string>; // Tag-agnostic: an SVG element answers with its own tag set
    declare context?: UIContext;

    canOverwrite(existingChild: BaseUIElement): boolean {
        return this.constructor === existingChild.constructor &&
               this.getNodeType() === existingChild.getNodeType();
    }

    applyRef(): void {
        // createElement resolves a string ref against the render stack before options are ever read
        if (this.options?.ref) {
            const obj = this.options.ref.parent;
            const name = this.options.ref.name ?? this.options.ref.key; // TODO: should be key
            obj[name] = this;
        }

        // We do this here since this is done on every redraw, and we just needed a way to hook into all redraw
        this.cancelEnqueuedRedraw();
    }

    removeRef(): void {
        if (this.options?.ref) {
            const obj = this.options.ref.parent;
            const name = this.options.ref.name;
            if (obj[name] === this) {
                obj[name] = undefined;
            }
        }
    }

    // Calls a queueMicrotask(() => this.redraw()), but only if one isn't already enqueued
    // The enqueued task will be canceled if a redraw is manually called in the meantime
    enqueueRedraw(event?: unknown): void {
        redrawPerTickRunner.maybeEnqueue(this, event);
    }

    cancelEnqueuedRedraw(): void {
        redrawPerTickRunner.clear(this);
    }

    // Lifecycle methods, called when the element was first inserted in the DOM, and before it's removed
    onMount(): void {}

    onUnmount(): void {}

    abstract getNodeType(): string | number;

    abstract mount(parent: UIElement<any, any, any> | HTMLElement, nextSibling?: Node | null): void;

    abstract redraw(event?: unknown): void;

    abstract createNode(): NodeType;

    abstract copyState(element: BaseUIElement): void;

    destroyNode(): void {
        this.dispatch("unmount", this);
        this.onUnmount();
        this.cleanup();
        this.removeRef();
        this.node?.remove();
        delete this.node; // Clear for gc
    }
}

// The value is stringified on read, so a number is as good a one as a string
export type TextElementOptions = {value?: string | number};

export class TextUIElement<ExtraOptions extends TextElementOptions = TextElementOptions, ValueType = string | number> extends BaseUIElement<Text> {
    value: ValueType;
    declare options?: ExtraOptions & UIElementOptions;

    constructor(value: ValueType | TextElementOptions = "" as ValueType) {
        super();
        if (value?.hasOwnProperty("value") && isPlainObject(value)) {
            this.value = (value as TextElementOptions).value as ValueType;
            this.options = value as any;
        } else {
            this.value = (value ?? "") as ValueType;
        }
    }

    mount(parent: UIElement, nextSibling?: Node | null): void {
        this.parent = parent;
        if (!this.node) {
            this.createNode();
            this.applyRef();
        } else {
            this.redraw();
        }
        parent.node!.insertBefore(this.node!, nextSibling);
        this.onMount();
    }

    getNodeType(): number {
        return Node.TEXT_NODE;
    }

    copyState(element: TextUIElement<any, ValueType>): void {
        this.value = element.value;
        this.options = element.options;
    }

    createNode(): Text {
        this.node = document.createTextNode(this.getValue());
        applyDebugFlags(this);
        return this.node;
    }

    setValue(value: ValueType): void {
        this.value = (value != null) ? value : "" as ValueType;
        if (this.node) {
            this.redraw();
        }
    }

    getValue(): string {
        return String(this.value);
    }

    toString(): string {
        return this.getValue();
    }

    redraw(): void {
        if (this.node) {
            let newValue = this.getValue();
            // TODO: check if this is best for performance
            if (this.node.nodeValue !== newValue) {
                this.node.nodeValue = newValue;
            }
        }
        this.applyRef();
    }
}

UI.TextElement = TextUIElement;

export class UIElement<
    ExtraOptions = {},
    NodeType extends (HTMLElement | SVGElement) = HTMLElement,
    TagType extends string = HTMLTagType,
    OptionsType extends UIElementOptions<TagType> = UIOptions<NodeType, ExtraOptions, TagType>
> extends BaseUIElement<NodeType> {
    static domAttributesMap: DOMAttributesMap = NodeAttributes.defaultAttributesMap;
    static nodeEventsMap: DOMAttributesMap = NodeAttributes.defaultEventsMap;
    static theme?: Theme;

    children: BaseUIElement[] = [];
    declare options: OptionsType;
    // Written by addListenersFromOptions, one per option it has wired; nothing else reads them
    [handler: `on${string}AutoHandler`]: ((...args: any[]) => void) | undefined;

    // What is held, not what a tag takes: only createElement normalizes, and it has by the time it constructs
    constructor(options: OptionsType = {} as OptionsType) {
        super();
        this.children = [];  // These are the rendered children
        this.options = options; // TODO: this is a hack, to not break all the code that references this.options in setOptions
        this.setOptions(options); // TODO maybe this actually needs to be removed, since on a copy we don't want the default options of the other object
    }

    // Not Partial<this["options"]>: a `this` return type stays deferred, so no subclass could satisfy it -
    // measured again at 51 errors, every one a `this` assignability cascade and not a single option typo
    getDefaultOptions(_options?: this["options"]): Record<string, any> | void {}

    // Return our options without the UI specific fields, so they can be passed along
    getCleanedOptions(): Partial<this["options"]> {
        const options = {
            ...this.options,
        };

        delete options.ref;
        delete options.children;
        delete options.key;
        delete options.nodeType;

        return options;
    }

    getPreservedOptions(): Partial<this["options"]> | void {}

    setOptions(options: this["options"]): void {
        const defaultOptions = this.getDefaultOptions(options);
        if (defaultOptions) {
            options = Object.assign(defaultOptions, options);
        }
        this.options = options;
    }

    // TODO: should probably add a second arg, doRedraw=true - same for setOptions
    // Generic over the keys given, so each value is still checked against its own declared type
    updateOptions<Key extends keyof this["options"]>(options: {[P in Key]?: this["options"][P]}): void {
        this.setOptions(Object.assign(this.options, options));
        // TODO: if the old options and the new options are deep equal, we can skip this redraw, right?
        this.redraw();
    }

    setChildren(...args: UIChild[]): void {
        this.updateOptions({children: cleanChildren(args)});
    }

    // Used when we want to reuse the current element, with the options from the passed in argument
    // Is only called when element.canOverwrite(this) is true
    copyState(element: typeof this): void {
        let options = element.options;
        let preservedOptions = this.getPreservedOptions();
        if (preservedOptions) {
            options = {...options, ...preservedOptions};
        }
        this.setOptions(options || {} as OptionsType);
        this.addListenersFromOptions();
    }

    // SVG elements create their nodes with createElementNS, so they answer with a different tag set
    getNodeType(): TagType {
        return (this.options?.nodeType || "div") as TagType;
    }

    // Constructs directly rather than through createElement, so what it takes is the cleaned form already
    static create<T extends UIElement<any>>(this: new (options?: PartialOptions<T>) => T, parentNode: UIElement | HTMLElement, options?: PartialOptions<T>): T {
        const uiElement = new this(options);
        uiElement.mount(parentNode, null);
        uiElement.dispatch("mount", uiElement);
        return uiElement;
    }

    // TODO: should be renamed to renderContent
    getGivenChildren(): UIChild {
        return this.options?.children || [];
    }

    render(): UIChild {
        return this.options?.children;
    }

    getTitle(): UIChild {
        return this.options?.title;
    }

    createNode(): NodeType {
        this.node = document.createElement(this.getNodeType() as HTMLTagType) as NodeType;
        applyDebugFlags(this);
        return this.node;
    }

    // Abstract, gets called when removing DOM node associated with the
    cleanup(): void {
        this.runCleanupJobs();
        for (const child of this.children) {
            child.destroyNode();
        }
        this.clearNode();
        super.cleanup();
    }

    overwriteChild<ChildType extends BaseUIElement>(existingChild: ChildType, newChild: ChildType): ChildType {
        existingChild.copyState(newChild);
        return existingChild;
    }

    getElementKeyMap(elements: BaseUIElement[]): Map<string | number, BaseUIElement> | null {
        if (!Array.isArray(elements)) {
            return null;
        }
        const childrenKeyMap = new Map<string | number, BaseUIElement>();

        for (let i = 0; i < elements.length; i += 1) {
            const childKey = elements[i].options?.key || ("autokey" + i); // TODO @cleanup an element shouldn't have missing options

            childrenKeyMap.set(childKey, elements[i]);
        }

        return childrenKeyMap;
    }

    getChildrenToRender(): UIChild {
        return this.render();
    }

    getExtraContext(): UIContext | null {
        const theme = this.options?.theme;
        if (theme) {
            return {theme};
        }
        return null;  // cleanObject({theme}, {emptyAsNull: true});
    }

    updateContext(context: UIContext = this.parent?.context): void {
        const extraContext = this.getExtraContext();
        this.context = extraContext ? {...context, ...extraContext} : context;
    }

    getChildrenForRedraw(): UIResolvedChild[] {
        RenderStack.push(this);
        const children = cleanChildren(this.getChildrenToRender());
        RenderStack.pop();
        return children;
    }

    // The overload lets an override take redrawPerTickRunner's event without this body declaring one
    redraw(event?: unknown): boolean | void;
    redraw(): boolean | void {
        if (!this.node) {
            console.error("Element not yet mounted. Redraw aborted!", this);
            return false;
        }

        this.updateContext();

        let newChildren = this.getChildrenForRedraw();

        if (newChildren === this.children) {
            for (const child of this.children) {
                child.redraw();
            }

            this.applyNodeAttributes();
            this.applyRef();

            return true;
        }

        const domNode = this.node;
        const childrenKeyMap = this.getElementKeyMap(this.children);
        // The resolved child the last turn wrote back, which is what newChildren[i - 1] holds
        let prevChild: BaseUIElement | null = null;

        for (let i = 0; i < newChildren.length; i++) {
            let newChild = resolveChildElement(newChildren[i], this);
            let prevChildNode = prevChild ? prevChild.node : null;
            let currentChildNode = (prevChildNode) ? prevChildNode.nextSibling : domNode.firstChild;

            // Written back, since cleanChildren may have answered with the caller's own array and the
            // identity with options.children is what appendChild and its siblings check for
            if (newChild !== newChildren[i]) {
                newChildren[i] = newChild;
            }

            const newChildKey = newChild.options?.key || ("autokey" + i);
            const existingChild = childrenKeyMap?.get(newChildKey);

            if (existingChild && newChild.canOverwrite(existingChild)) {
                // We're replacing an existing child element, it might be the very same object
                if (existingChild !== newChild) {
                    newChildren[i] = newChild = this.overwriteChild(existingChild, newChild);
                }
                newChild.redraw();
                if (newChild.node !== currentChildNode) {
                    domNode.insertBefore(newChild.node!, currentChildNode);
                }
            } else {
                // Getting here means we are not replacing anything, should just render
                newChild.mount(this, currentChildNode);
            }
            prevChild = newChild;
        }

        if (this.children.length) {
            // Remove children that don't need to be here
            let newChildrenSet = new Set(newChildren);

            for (const currentChild of this.children) {
                if (!newChildrenSet.has(currentChild)) {
                    currentChild.destroyNode();
                }
            }
        }

        // The loop above resolved every entry to an element and wrote it back, which is a narrowing of the
        // array in place - the one thing here that has to be asserted rather than shown
        this.children = newChildren as BaseUIElement[];

        // TODO this end logic is duplicated
        this.applyNodeAttributes();
        this.applyRef();

        return true;
    }

    // TODO This is actually slightly wrong, since we need to reuse the attr object we previously created
    getOptionsAsNodeAttributes(): NodeAttributes {
        return setObjectPrototype(this.options || {}, NodeAttributes);
    }

    instantiateNodeAttributes(): NodeAttributes {
        return new NodeAttributes(this.options);
    }

    getNodeAttributes(): NodeAttributes {
        const attr = this.instantiateNodeAttributes();
        // Add the default class "container" from our style sheet (if there is one)
        const containerClassName = this.styleSheet?.container;
        if (containerClassName) {
            attr.addClass(containerClassName);
        }
        return attr;
    }

    extraNodeAttributes(_attr?: NodeAttributes): void {}

    applyNodeAttributes(): void {
        const attr = this.getNodeAttributes();
        this.extraNodeAttributes(attr);
        attr.apply(this.node, this.constructor.domAttributesMap);
    }

    setAttribute(key: string, value: unknown): void {
        this.getOptionsAsNodeAttributes().setAttribute(key, value, this.node, this.constructor.domAttributesMap);
    }

    setStyle(key: string | Record<string, StyleValue<string>>, value?: StyleValue<string>): void {
        if (typeof key === "object") {
            for (const [styleKey, styleValue] of Array.from(Object.entries(key))) {
                this.setStyle(styleKey, styleValue);
            }
            return;
        }
        this.getOptionsAsNodeAttributes().setStyle(key, value, this.node);
    }

    removeStyle(key: string): void {
        this.getOptionsAsNodeAttributes().removeStyle(key, this.node);
    }

    addClass(className: string): void {
        this.getOptionsAsNodeAttributes().addClass(className, this.node);
    }

    removeClass(className: string): void {
        this.getOptionsAsNodeAttributes().removeClass(className, this.node);
    }

    hasClass(className: string): boolean {
        return this.getOptionsAsNodeAttributes().hasClass(className);
    }

    toggleClass(className: string): void {
        if (!this.hasClass(className)) {
            this.addClass(className);
        } else {
            this.removeClass(className);
        }
    }

    getTheme(): Theme {
        return this.options?.theme || this.context?.theme || Theme.Global;
    }

    get styleSheet(): Nullable<StyleSheet> {
        let {styleSheet} = this.options || {};
        const theme = this.getTheme();

        if (!styleSheet) {
            styleSheet = theme.getStyleSheet(this.constructor);
        }
        return styleSheet?.getInstance(theme);
    }

    get themeProps(): ThemeProps {
        return (this.options?.styleSheet as StyleSheet)?.themeProps || this.getTheme().props;
    }

    addListenersFromOptions() {
        for (const key in this.options) {
            if (typeof key === "string" && key.startsWith("on") && key.length > 2) {
                const eventType = key.substring(2);

                const addListenerMethodName: `add${string}Listener` = `add${eventType}Listener`;
                const handlerMethodName: `on${string}AutoHandler` = `on${eventType}AutoHandler`;

                // We create a wrapper handler, to not worry about updates changing the callback.
                // The handlerMethod might have been previously added
                // by a previous call to this function or manually by the user
                if (this[handlerMethodName]) {
                    // Don't double add
                    continue;
                }

                // Left standing: an `add${string}Listener` index signature would swallow every adder name in EventOptions
                const haveListenerMethod = (typeof this[addListenerMethodName] === "function");
                const nodeEvent = this.constructor.nodeEventsMap.getKeyFromDOMName(eventType.toLowerCase());

                if (haveListenerMethod || nodeEvent) {
                    this[handlerMethodName] = (...args: any[]) => {
                        if (isFunction(this.options[key])) {
                            this.options[key](...args, this);
                        }
                    };

                    // Actually add the listener
                    if (haveListenerMethod) {
                        this[addListenerMethodName](this[handlerMethodName]);
                    } else {
                        this.addNodeListener(eventType.toLowerCase(), this[handlerMethodName]);
                    }
                }
            }
        }
    }

    refLink(name: string): RefLinkOptions {
        return {parent: this, name: name};
    }

    refLinkArray(arrayName: string, index: number): RefLinkOptions {
        if (!this[arrayName]) {
            this[arrayName] = [];
        }
        return {parent: this[arrayName], name: index};
    }

    bindToNode(node: HTMLElement, doRedraw?: boolean): this {
        this.node = node as NodeType;
        if (doRedraw) {
            this.clearNode();
            this.redraw();
        }
        return this;
    }

    mount(parentNode: UIElement<any, any, any> | HTMLElement, nextSiblingNode?: Node | null): void {
        const parent = (parentNode instanceof HTMLElement) ? new UIElement().bindToNode(parentNode) : parentNode;
        this.parent = parent;
        if (this.node) {
            parent.insertChildNodeBefore(this, nextSiblingNode);
            this.dispatch("changeParent", this.parent);
            return;
        }

        this.createNode();
        this.redraw();

        parent.insertChildNodeBefore(this, nextSiblingNode || null);

        this.addListenersFromOptions();

        this.onMount();
    }

    // You need to overwrite the next child manipulation routines if this.options.children !== this.children
    appendChild(child: BaseUIElement): BaseUIElement {
        // TODO: the next check should be done with a decorator
        if (this.children !== this.options?.children) {
            throw "Can't properly handle appendChild, you need to implement it for " + this.constructor;
        }
        this.options.children = this.options.children || [];
        this.options.children.push(child);
        child.mount(this, null);
        return child;
    }

    insertChild(child: BaseUIElement, position?: number): BaseUIElement {
        if (this.children !== this.options?.children) {
            throw "Can't properly handle insertChild, you need to implement it for " + this.constructor;
        }
        position = position || 0;

        this.options.children = this.options.children || [];
        this.options.children.splice(position, 0, child);

        const nextChildNode = position + 1 < this.options.children.length ? this.children[position + 1].node : null;

        (child as UIElement).mount(this, nextChildNode);

        return child;
    }

    eraseChild(child: BaseUIElement, destroy: boolean = true): BaseUIElement | null {
        if (!this.options?.children) return null;
        let index = this.options.children.indexOf(child);

        if (index < 0) {
            // child not found
            return null;
        }
        return this.eraseChildAtIndex(index, destroy);
    }

    eraseChildAtIndex(index: number, destroy: boolean = true): BaseUIElement | null {
        if (!this.options?.children || index < 0 || index >= this.options.children.length) {
            console.error("Erasing child at invalid index ", index, this.options.children?.length || 0);
            return null;
        }
        if (this.children !== this.options.children) {
            throw "Can't properly handle eraseChild, you need to implement it for " + this.constructor;
        }
        let erasedChild = this.options.children.splice(index, 1)[0] as BaseUIElement;
        if (destroy) {
            erasedChild.destroyNode();
        } else {
            this.node.removeChild(erasedChild.node);
        }
        return erasedChild;
    }

    show(): void {
        this.removeClass("hidden");
    }

    hide(): void {
        this.addClass("hidden");
    }

    insertChildNodeBefore(childElement: BaseUIElement, nextSiblingNode: Node | null): void {
        this.node!.insertBefore(childElement.node!, nextSiblingNode);
    }

    // TODO: should be renamed emptyNode()
    clearNode(): void {
        while (this.node?.lastChild) {
            this.node.removeChild(this.node.lastChild);
        }
    }

    isInDocument(): boolean {
        return document.body.contains(this.node!);
    }

    // TODO: this method also doesn't belong here
    getWidthOrHeight(parameter: "width" | "height"): number {
        let node = this.node as HTMLElement;
        if (!node) {
            return 0;
        }
        let value = parseFloat(parameter === "width" ? String(node.offsetWidth) : String(node.offsetHeight));
        return value || 0;
    }

    getHeight(): number {
        return this.getWidthOrHeight("height");
    }

    getWidth(): number {
        return this.getWidthOrHeight("width");
    }

    setHeight(value: number | string): void {
        this.setStyle("height", suffixNumber(value, "px"));
        this.dispatch("resize");
    }

    setWidth(value: number | string): void {
        this.setStyle("width", suffixNumber(value, "px"));
        this.dispatch("resize");
    }

    // A known DOM event name says which event the callback gets, so a listener does not have to widen it
    addNodeListener<Name extends keyof HTMLElementEventMap>(name: Name, callback: (event: HTMLElementEventMap[Name]) => void, ...args: NodeListenerOptions): RemoveHandle;
    addNodeListener(name: string, callback: EventListener, ...args: NodeListenerOptions): RemoveHandle;
    addNodeListener(name: string, callback: EventListener, ...args: NodeListenerOptions): RemoveHandle {
        (this.node as HTMLElement).addEventListener(name, callback, ...args);
        const handler = {
            remove: () => {
                this.removeNodeListener(name, callback, ...args);
            }
        };
        this.addCleanupJob(handler);
        return handler;
    }

    // The options go back too: capture is part of a listener's identity, so removing without it removes nothing
    removeNodeListener(name: string, callback: EventListener, ...args: NodeListenerOptions): void {
        this.node?.removeEventListener(name, callback, ...args);
    }

    // TODO: methods can be automatically generated by addNodeListener(UI.Element, "dblclick", "DoubleClick") for instance
    addClickListener(callback: (event: MouseEvent) => unknown): RemoveHandle {
        return this.addNodeListener("click", callback);
    }

    removeClickListener(callback: EventListener): void {
        this.removeNodeListener("click", callback);
    }

    addPressStartListener(callback: EventListener): CleanupJobs {
        return new CleanupJobs([
            this.addNodeListener("mousedown", callback),
            this.addNodeListener("touchstart", callback)
        ]);
    }

    removePressStartListener(callback: EventListener): void {
        this.removeNodeListener("mousedown", callback);
        this.removeNodeListener("touchstart", callback);
    }

    addPressStopListener(callback: EventListener): CleanupJobs {
        return new CleanupJobs([
            this.addNodeListener("mouseup", callback),
            this.addNodeListener("touchend", callback)
        ]);
    }

    removePressStopListener(callback: EventListener): void {
        this.removeNodeListener("mouseup", callback);
        this.removeNodeListener("touchend", callback);
    }

    // Named so EventOptions derives them; the nodeEventsMap fallback serves the same two events
    addMouseEnterListener(callback: (event: MouseEvent) => void): RemoveHandle {
        return this.addNodeListener("mouseenter", callback);
    }

    addMouseLeaveListener(callback: (event: MouseEvent) => void): RemoveHandle {
        return this.addNodeListener("mouseleave", callback);
    }

    addDoubleClickListener(callback: EventListener): RemoveHandle {
        return this.addNodeListener("dblclick", callback);
    }

    removeDoubleClickListener(callback: EventListener): void {
        this.removeNodeListener("dblclick", callback);
    }
}

const SVG_TAGS = new Set(["svg", "g", "defs", "radialGradient", "stop", "ellipse", "clipPath", "ellipse", "path", "text"]); // TODO @types full set

function isSVGTag(tag: string): tag is SVGTagType {
    return SVG_TAGS.has(tag);
}

// Written once to cover every declared overload, which is why the implementation type is looser
UI.createElement = function (tag: typeof BaseUIElement<any> | HTMLTagType | SVGTagType, options?: WrittenUIElementOptions | null, ...children: UIChild[]): BaseUIElement | null {
    if (!tag) {
        console.error("Create element needs a valid object tag, did you mistype a class name?");
        return null;
    }

    options = options || {} as WrittenUIElementOptions;

    options.children = cleanChildren(children);

    if (options.ref) {
        if (typeof options.ref === "string") {
            if (RenderStack.length > 0) {
                options.ref = {
                    parent: RenderStack[RenderStack.length - 1] as UIElement<any>,
                    name: options.ref
                };
            } else {
                throw Error("Failed to automatically link ref, there needs to be an element in the rendering stack");
            }
        }

        if (options.key) {
            console.error("Warning! UI Element cannot have both a key and a ref fieldname. Key will be overriden.\n" +
                          "Are you using the options from another object? Shame!", options);
        }

        options.key = "_ref" + options.ref.name;
    }

    if (options.hasOwnProperty("class")) {
        console.error("Invalid UI Element attribute: class. Did you mean className?");
    }

    if (isString(tag)) {
        options.nodeType = tag as HTMLTagType; // An SVG tag lands here too, and SVGUIElement narrows it back
        if (isSVGTag(tag)) {
            return new UI.SVGElement(options as any);
        }
        return new UIElement<void, HTMLElementTagNameMap[typeof tag]>(options as any);
    }

    return new (tag as typeof UIElement)(options as UIElementOptions); // Normalized above, so this is what is held
} as UINamespace["createElement"];

UI.Element = UIElement;


// Keep a map for every base class, and for each base class keep a map for each nodeType, to cache classes
const primitiveMap: WeakMap<typeof UIElement, Map<string, typeof UIElement<any>>> = new WeakMap();

UI.Primitive = (<T extends keyof HTMLElementTagNameMap = keyof HTMLElementTagNameMap, BaseClassType extends typeof UIElement = typeof UIElement>(nodeType: T, BaseClass: BaseClassType = UIElement as BaseClassType): BaseClassType => {
    let baseClassPrimitiveMap = primitiveMap.get(BaseClass);
    if (!baseClassPrimitiveMap) {
        baseClassPrimitiveMap = new Map();
        primitiveMap.set(BaseClass, baseClassPrimitiveMap);
    }
    let resultClass = baseClassPrimitiveMap.get(nodeType);
    if (resultClass) {
        return resultClass as any;
    }
    // Cast off the type parameter: a class expression can only extend a type variable that constructs from (...args: any[])
    resultClass = class Primitive extends (BaseClass as typeof UIElement)<{}, HTMLElementTagNameMap[T]> {
        declare node?: HTMLElementTagNameMap[T];
        
        getNodeType(): T {
            return nodeType;
        }
        
        createNode(): HTMLElementTagNameMap[T] {
            this.node = document.createElement(nodeType);
            applyDebugFlags(this);
            return this.node;
        }
    };
    baseClassPrimitiveMap.set(nodeType, resultClass);
    return resultClass as any;
}) as UINamespace["Primitive"];

// Hung on the node under STEM_DEBUG, so the element behind a node can be found from the console
declare global {
    interface Node {
        stemElement?: BaseUIElement;
    }
}

export function applyDebugFlags(element: BaseUIElement): void {
    if (globalThis.STEM_DEBUG && element.node) {
        element.node.stemElement = element;
    }
}
